import badgeNewMember from "@/shared/assets/img/badges/badge-new-member.png";
import badgeVerified from "@/shared/assets/img/badges/badge-verified.png";
import badgeTransparentOrg from "@/shared/assets/img/badges/badge-transparent-org.png";
import badgeActiveContributor from "@/shared/assets/img/badges/badge-active-contributor.png";
import badgeComplianceCertified from "@/shared/assets/img/badges/badge-compliance-certified.png";
import badgeTrustedPartner from "@/shared/assets/img/badges/badge-trusted-partner.png";
import logoNewMember from "@/shared/assets/img/logo/new-member.png";
import logoVerified from "@/shared/assets/img/logo/verified.png";
import logoTransparentOrg from "@/shared/assets/img/logo/transparent-organization.png";
import logoActiveContributor from "@/shared/assets/img/logo/active-contributor.png";
import logoComplianceCertified from "@/shared/assets/img/logo/compliance-certified.png";
import logoTrustedPartner from "@/shared/assets/img/logo/trusted-partner.png";
import type { RatingBadge } from "@/shared/types";

interface RatingBadgeConfig {
  image: string;
  /** The organization's logo for this level — the brand mark inside a
   *  level-colored ring. Shown in the sidebar and in the header account
   *  dropdown, where it stands in for an avatar. */
  logoImage: string;
  /** Key in the Sidebar namespace — the level name, shown next to the icon.
   *  The wording matches the control-plane admin panel, so an operator and the
   *  organization see the same level name. */
  labelKey: string;
  /** Keys in the MyOrganizationPage namespace — what earns this level. */
  criteriaKeys: string[];
}

/** Lowest to highest. The order is the ladder shown on the organization page,
 *  and it is what decides which levels count as already passed. */
export const RATING_BADGE_ORDER: RatingBadge[] = [
  "ngo_new_member",
  "ngo_verified",
  "ngo_transparent_org",
  "ngo_active_contributor",
  "ngo_compliance_certified",
  "ngo_trusted_partner",
];

export const RATING_BADGE_CONFIG: Record<RatingBadge, RatingBadgeConfig> = {
  ngo_new_member: {
    image: badgeNewMember,
    logoImage: logoNewMember,
    labelKey: "ratingBadge.newMember",
    criteriaKeys: ["rating.criteria.newMember.1", "rating.criteria.newMember.2", "rating.criteria.newMember.3"],
  },
  ngo_verified: {
    image: badgeVerified,
    logoImage: logoVerified,
    labelKey: "ratingBadge.verified",
    criteriaKeys: ["rating.criteria.verified.1", "rating.criteria.verified.2", "rating.criteria.verified.3"],
  },
  ngo_transparent_org: {
    image: badgeTransparentOrg,
    logoImage: logoTransparentOrg,
    labelKey: "ratingBadge.transparentOrg",
    criteriaKeys: [
      "rating.criteria.transparentOrg.1",
      "rating.criteria.transparentOrg.2",
      "rating.criteria.transparentOrg.3",
      "rating.criteria.transparentOrg.4",
    ],
  },
  ngo_active_contributor: {
    image: badgeActiveContributor,
    logoImage: logoActiveContributor,
    labelKey: "ratingBadge.activeContributor",
    criteriaKeys: [
      "rating.criteria.activeContributor.1",
      "rating.criteria.activeContributor.2",
      "rating.criteria.activeContributor.3",
      "rating.criteria.activeContributor.4",
    ],
  },
  ngo_compliance_certified: {
    image: badgeComplianceCertified,
    logoImage: logoComplianceCertified,
    labelKey: "ratingBadge.complianceCertified",
    criteriaKeys: [
      "rating.criteria.complianceCertified.1",
      "rating.criteria.complianceCertified.2",
      "rating.criteria.complianceCertified.3",
    ],
  },
  ngo_trusted_partner: {
    image: badgeTrustedPartner,
    logoImage: logoTrustedPartner,
    labelKey: "ratingBadge.trustedPartner",
    criteriaKeys: [
      "rating.criteria.trustedPartner.1",
      "rating.criteria.trustedPartner.2",
      "rating.criteria.trustedPartner.3",
      "rating.criteria.trustedPartner.4",
    ],
  },
};

/** The organization's logo and the label for its `title`/`alt`, for the level
 *  it is on right now. The single place that knows what to show when the level
 *  is unknown: no organization linked yet, `/api/auth/me/` not resolved, or a
 *  value outside the E-NGO scale (a legacy `bronze` left over from before the
 *  product-scoped split) — all of them fall back to the lowest level. */
export function getOrgLogo(ratingBadge?: RatingBadge | null) {
  const config = (ratingBadge && RATING_BADGE_CONFIG[ratingBadge]) || RATING_BADGE_CONFIG.ngo_new_member;
  return { image: config.logoImage, labelKey: config.labelKey };
}
